import { abilityByNumber } from "odyssey-shared";
import type { FightSounds } from "./audio/cues.ts";
import { bindPlayerBar } from "./bar/binding.ts";
import { destroyRefusalLine, dropRefusalLine, itemRefusalLine, moveRefusalLine } from "./bag/describe.ts";
import { Fight } from "./fight/fight.ts";
import { PAGE_HITS } from "./fight/page-hits.ts";
import type { StanceStore } from "./fight/stance.ts";
import type { PageTargets } from "./page-targets.ts";
import { gameReport } from "./report.ts";
import { PAGE_RULES } from "./rules.ts";
import type { SessionCore, SessionOptions, SessionParts } from "./session-types.ts";
import { shownName } from "./shown-name.ts";
import { ownNow } from "./own-clock.ts";
import type { VfxPlayer } from "./vfx/player.ts";
import { ownDrink } from "./vfx/triggers.ts";

/**
 * What the page does when the local character dies: the chat gets the death line, the death screen the cause, and the tool
 * belt's fan closes, as a tool cannot be chosen while dead.
 *
 * @param options The parts of the page that hear of a death.
 * @param line The death line for the chat.
 * @param cause The death screen's cause.
 */
export function tellDeath(options: Pick<SessionOptions, "chat" | "death" | "belt">, line: string, cause: string): void {
  options.chat?.death(line);
  options.death?.cause(cause);
  options.belt?.setFan(false);
}

/**
 * Makes the fight on the page: the swing, the combat frame's events, the player's own death and respawn, and its
 * effects (`fight/fight.ts`), over the local player's body, the player's target and the stance. It sends an act on
 * the current connection once the world is shown, names its characters as the view and the world's look table hold
 * them, aims from the keys' movement intent, and tells the bar the cooldowns and refusals, the death screen and the
 * chat the death, and the sound the fight's cues.
 *
 * @param core What the session builds first.
 * @param targets The player's target.
 * @param stance The weapon drawn or sheathed.
 * @param fightSounds The fight's sounds, or undefined without sounds.
 * @param vfx The vfx player a potion's drink calls (P2.8-T89).
 * @returns The fight.
 */
export function createFight(core: SessionCore, targets: PageTargets, stance: StanceStore, fightSounds: FightSounds | undefined, vfx: VfxPlayer): Fight {
  const { options, host, state, connection, local, gate } = core;
  return new Fight(
    {
      body: local,
      say: (text) => host.messageLine.say(text),
      onDeath: (line, cause) => tellDeath(options, line, cause),
      hits: PAGE_HITS,
      nameOf: (handle) => shownName(connection()?.view.entities.get(handle), host.world()?.looks ?? [], "full", host.world()?.npcLooks ?? []),
      chatNameOf: (handle) => shownName(connection()?.view.entities.get(handle), host.world()?.looks ?? [], "first", host.world()?.npcLooks ?? []),
      ready: () => state.revealed && (connection()?.ready ?? false),
      target: () => targets.handle,
      heading: () => host.intent(),
      send: (afterSeq, ability, target, aim) => connection()?.sendAct(afterSeq, ability, target, aim),
      targetPoint: (out) => targets.feet(out),
      onUsed: (ability, ticks) => {
        options.bar?.used(ability, ticks, ownNow());
        options.sounds?.itemUsed(ability);
        const drink = abilityByNumber(PAGE_RULES, ability)?.drink;
        if (drink !== null && drink !== undefined) {
          ownDrink(vfx, state.handle, drink);
        }
      },
      onCooldown: (ability, ticks) => options.bar?.told(ability, ticks, ownNow()),
      onRefused: (ability, reason, cancelled) => options.bar?.refused(ability, reason, cancelled),
      ...(fightSounds === undefined ? {} : { sound: fightSounds }),
      stance,
      gate,
    },
    PAGE_RULES,
  );
}

/**
 * Binds the fight to the stores that reach it: a slot's use on the bar (P2.2-T4) and the player's bar, which tells the
 * fight which slots hold an ability (P2.8-T29, ADR-121); the bag's refusals, said on the message line with the
 * fight's refusal sound; and the stance (P2.2-T13), which the report shows and the local player wears.
 *
 * @param parts The session's parts.
 * @param fightSounds The fight's sounds, or undefined without sounds.
 */
export function bindFight(parts: SessionParts, fightSounds: FightSounds | undefined): void {
  const { options, host, state, local, fight, stance } = parts;
  options.bar?.bindUse((slot) => state.revealed && fight.useAtTarget(slot, host.now()));
  if (options.bar !== undefined && options.bag !== undefined) {
    bindPlayerBar(options.bar, options.bag, PAGE_RULES, options.arranged ?? null, (specs) => fight.setBar(specs.map((spec) => (spec.item === null ? spec.ability : 0))));
  }
  if (options.bag !== undefined) {
    options.bag.onRefused((refusal) => {
      if (refusal.request === "split" || refusal.request === "sort" || refusal.request === "keep") {
        return;
      }
      const line = refusal.request === "use" ? itemRefusalLine(refusal.reason) : refusal.request === "destroy" ? destroyRefusalLine(refusal.reason) : refusal.request === "drop" ? dropRefusalLine(refusal.reason) : moveRefusalLine(refusal.reason);
      if (line !== null) {
        host.messageLine.say(line);
      }
      if (refusal.request === "use" || line !== null) {
        fightSounds?.refused();
      }
    });
  }
  stance.bindToggle(() => state.revealed && fight.toggleStance());
  stance.subscribe(() => {
    gameReport.stance = stance.getSnapshot();
    local.wear(stance.getSnapshot() === "drawn");
  });
  local.wear(stance.getSnapshot() === "drawn");
  gameReport.stance = stance.getSnapshot();
}
